/**
 * Theo brand tokens — the single source for color and type across the app shell.
 *
 * Grammar (keep it, or the palette stops meaning anything):
 * - Plum is the rail (sidebar). Neutrals carry a faint plum tint so content shares its DNA.
 * - Violet is the one interactive color (buttons, links, focus). Theo-violet marks Theo itself.
 * - Orange means "you are here" / "look at this": selection rails, current page, unread counts.
 *   Never a button fill.
 * - Brown/sand is people: human avatars and human-owned things.
 *
 * Text contrast on white (WCAG): text 15.8:1, textSecondary 6.6:1, textMuted 4.6:1, violet 6.1:1.
 */

export const PLUM = {
  rail: "#24163D",
  railDeep: "#1C1131",
  railWarm: "#2A1830",
  hover: "#30204F",
  line: "#35264F",
  selected: "#3A2862",
  selectedHover: "#43306E",
  /** Text and icons on the rail. INK 13:1, MUTED 7:1, GROUP 5.5:1 against `rail`. */
  ink: "#EEE8FF",
  muted: "#B6A8DA",
  group: "#9D8CC8",
} as const;

export const VIOLET = {
  /** Primary interactive color. Softened from #5B3CC4: solid fills read as heavy
   *  blocks next to the plum rail. White text on this is 6.1:1. */
  main: "#6450BE",
  dark: "#52409F",
  /** Theo's identity mark (the "Theo" label, AI-engineer column). Not for controls. */
  theo: "#9B7AEF",
  /** Theo's mark when it is text: `theo` is only 3.2:1 on white, this is 5.7:1. */
  theoText: "#6E4FD8",
  tint: "#EFE9FF",
  wash: "#F7F4FF",
} as const;

export const ORANGE = {
  main: "#FFA552",
  soft: "#FFC58F",
  wash: "#FFF4E8",
  /** Orange that is dark enough to be text on white (5.0:1). */
  text: "#B4560F",
} as const;

export const BROWN = {
  main: "#8A5A2B",
  /** A second people-hue for data viz, beside `main` (e.g. Chat Assistant next to ticket chat). */
  soft: "#C9A27A",
  sand: "#F3E6D8",
  /** Human avatar fills: each pairs with `ink` text at >= 4.5:1. */
  avatarFills: ["#F3E6D8", "#EAD7C3", "#F6DFC8", "#E8DCCF", "#F1D9C4"],
  ink: "#5A3A1C",
} as const;

export const NEUTRAL = {
  /** The canvas every page sits on. Tinted by *hue* not depth: barely off white
   *  (1.05:1 against surface) but violet-leaning, so it reads as plum-family
   *  rather than grey. Pages must use this, never their own off-white. */
  page: "#FAF8FE",
  surface: "#FFFFFF",
  subtle: "#FAF8FC",
  hover: "#F4F0F9",
  border: "#ECE7F3",
  borderStrong: "#DDD5EA",
  text: "#1F1830",
  textSecondary: "#625A78",
  textMuted: "#7A7090",
} as const;

export const FONT = {
  /** Defined once in global.css so the typeface is a single-line change. */
  sans: "var(--theo-font-sans)",
} as const;

/**
 * Corner radius scale. Kept deliberately tight: generous curves read as consumer
 * software, and this is a console technicians live in all day. `theme.shape.borderRadius`
 * is 3, so unitless `sx={{ borderRadius: n }}` resolves to n*3px.
 *
 * control 4px  - buttons, inputs, chips, icon buttons
 * card    8px  - cards, panels, dialogs, popovers
 * well   10px  - the few large containers that wrap other cards
 */
export const RADIUS = {
  control: 4,
  card: 8,
  well: 10,
} as const;

/** Flat by default. Depth comes from borders and the page tint, not drop shadows. */
export const SHADOW = {
  card: "0 1px 2px rgba(31, 24, 48, 0.04)",
  cardHover: "0 2px 6px rgba(31, 24, 48, 0.07)",
  popover: "0 4px 16px rgba(31, 24, 48, 0.10)",
} as const;

/** The sidebar's plum gradient, warming toward brown at the bottom. */
export const RAIL_BACKGROUND = `linear-gradient(180deg, ${PLUM.rail} 0%, ${PLUM.railDeep} 62%, ${PLUM.railWarm} 100%)`;

/** Shared "you are here" treatment: orange edge that follows the row's rounded corners. */
export const SELECTED_EDGE = `inset 3px 0 0 ${ORANGE.main}`;

/** Deterministic warm avatar fill for a person's name. */
export function humanAvatarFill(name: string): string {
  const fills = BROWN.avatarFills;
  if (!name) return fills[0];
  const hash = Array.from(name).reduce((acc, ch) => (acc * 31 + ch.charCodeAt(0)) >>> 0, 7);
  return fills[hash % fills.length];
}
